import React, { useContext, useEffect, useReducer, useState } from "react";
import MwInputText from "../components/ui/MwInputText";
import MwButton from "../components/ui/MwButton";
import MwModal from "../components/ui/MwModal";
import { useTranslation } from "react-i18next";
import i18next from "i18next";
import AccountCenterContext from "../utils/CostCenterAccHelper";
import { MwSpinnerButton } from "../components/ui";
import { useTenant } from "../hooks";

const initialFormState = {
  id: "",
  account_no: "",
  name_ar: "",
  name_en: "",
  level: 0,
  account_type: "primary",
};

const FormReducer = (state, action) => {
  switch (action.type) {
    case "SET_FIELD":
      return { ...state, [action.field]: action.value };
    case "RESET":
      return initialFormState;
    default:
      return state;
  }
};

export const CenterAccountMngPop = ({
  isOpen = false,
  setIsOpen = () => {},
  onClose = () => {},
  actionData,
  code = "",
  loading = false,
  loadingChild = false,
}) => {
  const [t] = useTranslation("global");
  const isViewMode = actionData?.mode === "view";

  const [formData, dispatchForm] = useReducer(FormReducer, initialFormState);
  const [errors, setErrors] = useState({});
  const { tenantId, companySelected, branchSelected } = useTenant();

  const { accounts, addAccount, editAccount, loadingSave } =
    useContext(AccountCenterContext);
  const isArabic = i18next.dir(i18next.language) === "rtl";

  const getNodeNameByLang = (node) => {
    if (isArabic) return node?.name_ar || node?.name_en || "";
    return node?.name_en || node?.name_ar || "";
  };

  const findAccount = (nodes, id, depth = 1) => {
    if (!Array.isArray(nodes)) return null;

    for (const node of nodes) {
      if (String(node?.id) === String(id)) return { node, depth };
      if (Array.isArray(node.childs) && node.childs.length) {
        const found = findAccount(node.childs, id, depth + 1);
        if (found) return found;
      }
    }
    return null;
  };
  const parentMatch = findAccount(accounts, actionData.parentId);
  const parentAccount = parentMatch?.node || null;
  const parentAccountDisplay = parentAccount
    ? `${getNodeNameByLang(parentAccount, isArabic)} - ${String(parentAccount?.account_no || "").trim()}`
    : "";
  const parentLevelNumber = Number(parentAccount?.level);
  const level = parentAccount
    ? (Number.isFinite(parentLevelNumber) && parentLevelNumber > 0
      ? parentLevelNumber + 1
      : (parentMatch?.depth || 0) + 1)
    : 1;
  const modalTitle =
    actionData?.mode === "add"
      ? t("accounts.addAccount")
      : actionData?.mode === "edit"
        ? t("accounts.editAccount")
        : actionData?.mode === "view"
          ? t("dropdown.preview")
          : "";
  const showParentAccountInTitle =
    actionData?.mode === "add" &&
    Boolean(actionData?.parentId) &&
    Boolean(parentAccountDisplay);

  const handleInputChange = (field, value) => {
    dispatchForm({ type: "SET_FIELD", field, value });

    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.account_no?.toString().trim())
      newErrors.account_no = `${t("accounts.accountNumber")} ${t("common.required")}`;

    if (!formData.name_ar.trim())
      newErrors.name_ar = `${t("accounts.accountNameAr")} ${t("common.required")}`;

    if (!formData.name_en.trim())
      newErrors.name_en = `${t("accounts.accountNameEn")} ${t("common.required")}`;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const normalizeAccountTypeForApi = (value) => {
    const normalized = String(value || "").toLowerCase();
    if (
      normalized === "secondary" ||
      normalized === "sub" ||
      normalized === "key"
    ) {
      return "key";
    }
    return "main";
  };
  const normalizeAccountTypeForForm = (value) => {
    const normalized = String(value || "").toLowerCase();
    if (
      normalized === "key" ||
      normalized === "sub" ||
      normalized === "secondary"
    ) {
      return "secondary";
    }
    return "primary";
  };

  const handleSave = async () => {
    if (isViewMode) return;
    if (!validateForm()) return;
    if (!tenantId || !companySelected?.value || !branchSelected?.value) {
      console.warn("Missing tenant/company/branch while saving cost center", {
        tenantId,
        companyId: companySelected?.value,
        branchId: branchSelected?.value,
      });
      return;
    }

    const payload = {
      parent_id:
        actionData.mode === "edit"
          ? (actionData?.account?.parent_id ?? 0)
          : (actionData?.parentId ?? 0),
      account_no: String(formData.account_no || "").trim(),
      name_ar: String(formData.name_ar || "").trim(),
      name_en: String(formData.name_en || "").trim(),
      account_type: normalizeAccountTypeForApi(formData.account_type),
      level:
        actionData.mode === "edit"
          ? (Number(actionData?.account?.level) > 0
            ? Number(actionData?.account?.level)
            : level)
          : level,
      tenant_id: tenantId,
      company_id: companySelected?.value,
      branch_id: branchSelected?.value,
    };
      
    try {
      if (actionData.mode === "add") {
        await addAccount(payload);
      } else if (actionData.mode === "edit") {
        const accountId = formData.id || actionData?.account?.id;
        await editAccount(payload, accountId);
      }

      dispatchForm({ type: "RESET" });
      setIsOpen(false);
    } catch (err) {
      console.error(
        "Failed to save cost center account",
        err?.response?.data || err?.message || err,
      );
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    dispatchForm({ type: "RESET" });
    errors && setErrors({});
    if (onClose) onClose();
  };
  useEffect(() => {
    if (
      (actionData?.mode === "edit" || actionData?.mode === "view") &&
      actionData.account
    ) {
      const account = actionData.account;

      dispatchForm({
        type: "SET_FIELD",
        field: "id",
        value: account.id || "",
      });
      dispatchForm({
        type: "SET_FIELD",
        field: "account_no",
        value: account.account_no || "",
      });

      dispatchForm({
        type: "SET_FIELD",
        field: "name_ar",
        value: account.name_ar || "",
      });

      dispatchForm({
        type: "SET_FIELD",
        field: "name_en",
        value: account.name_en || "",
      });

      dispatchForm({
        type: "SET_FIELD",
        field: "account_type",
        value: normalizeAccountTypeForForm(account.account_type),
      });
    }

    if (actionData?.mode === "add") {
      dispatchForm({ type: "RESET" });

      dispatchForm({
        type: "SET_FIELD",
        field: "account_type",
        value: actionData.accountType || "primary",
      });
      if (code) {
        dispatchForm({
          type: "SET_FIELD",
          field: "account_no",
          value: code,
        });
      }
    }
  }, [actionData, code]);
  if (loading || loadingChild || loadingSave) {
    return (
      <MwModal onShow={isOpen} setOnShow={setIsOpen}>
        <div className="w-full  h-[20vh] flex justify-center items-center">
          <MwSpinnerButton />
        </div>
      </MwModal>
    );
  }
  return (
    <MwModal onShow={isOpen} setOnShow={setIsOpen}>
      <div className="flex flex-col h-full w-full bg-white rounded-lg shadow-lg">
        <div className="p-4 border-b bg-slate-100">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="font-bold text-[#261759] text-lg">{modalTitle}</h2>
            {showParentAccountInTitle ? (
              <span className="text-sm font-medium text-slate-600">
                ({parentAccountDisplay})
              </span>
            ) : null}
          </div>
        </div>

        <div className="p-6 space-y-3 overflow-y-auto bg-slate-100">
          <div className="grid grid-cols-4 gap-4">
            <MwInputText
              label={t("accounts.accountNumber")}
              value={formData.account_no}
              onChange={(e) => handleInputChange("account_no", e.target.value)}
              placeholder="110000001"
              classNameInput="text-center"
              invalid={!!errors.account_no}
              inputType="number"
              invalidMsg={errors.account_no}
              dir="rtl"
              min={0}
              disabled={isViewMode}
            />

            <div className="relative mt-2">
              <label className="text-xs font-normal px-1 text-[#261759] pb-1 block">
                {t("accounts.accountType")}
              </label>

              <div className="flex gap-6 py-2 px-3 rounded-md">
                <label className="flex items-center gap-2 text-xs cursor-pointer text-gray-500 font-semibold">
                  <input
                    type="radio"
                    name="accountType"
                    value="primary"
                    disabled
                    checked={formData.account_type === "primary"}
                    onChange={(e) =>
                      handleInputChange("account_type", e.target.value)
                    }
                    className="cursor-pointer"
                  />
                  {t("accounts.primary")}
                </label>

                <label className="flex items-center gap-2 text-xs cursor-pointer text-gray-500 font-semibold">
                  <input
                    type="radio"
                    name="accountType"
                    value="secondary"
                    disabled
                    checked={formData.account_type === "secondary"}
                    onChange={(e) =>
                      handleInputChange("account_type", e.target.value)
                    }
                    className="cursor-pointer"
                  />
                  {t("accounts.sub")}
                </label>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <MwInputText
              id="name_ar"
              label={t("accounts.accountNameAr")}
              value={formData.name_ar}
              onChange={(e) => handleInputChange("name_ar", e.target.value)}
              placeholder={t("accounts.accountNameAr")}
              invalid={!!errors.name_ar}
              invalidMsg={errors.name_ar}
              inputType="text"
              dir="rtl"
              disabled={isViewMode}
            />

            <MwInputText
              id="name_en"
              label={t("accounts.accountNameEn")}
              value={formData.name_en}
              onChange={(e) => handleInputChange("name_en", e.target.value)}
              placeholder={t("accounts.accountNameEn")}
              invalid={!!errors.name_en}
              invalidMsg={errors.name_en}
              inputType="text"
              dir="ltr"
              disabled={isViewMode}
            />
          </div>
        </div>

        <div className="flex gap-2 bg-slate-200 p-4 rounded-b border-t border-slate-400 justify-end sticky bottom-0">
          <MwButton type="secondary" onClick={handleClose}>
            {t("common.cancel")}
          </MwButton>

          {!isViewMode ? (
            <MwButton type="primary" onClick={handleSave}>
              {t("common.save")}
            </MwButton>
          ) : null}
        </div>
      </div>
    </MwModal>
  );
};
